import { CheckboxGroupInput } from "@impero-com/common/components/form/fields/checkboxGroupInput";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Typography } from "antd";

import type {
    EffectivenessFieldTypeAnswer,
    SavedBlob,
} from "api/generated/model";
import { FileUploaderInput } from "components/form/fields/fileUploaderInput";
import { RadioButtonGroupInput } from "components/form/fields/radioButtonGroupInput";

import { ReadOnlyTaskWithAnswer } from "./types";

const { Text } = Typography;

interface ReadOnlyTaskFieldProps {
    field: ReadOnlyTaskWithAnswer;
    savedBlobs: SavedBlob[];
}
export function ReadOnlyTaskField({
    field,
    savedBlobs,
}: ReadOnlyTaskFieldProps) {
    const { t } = useTranslation();
    const noValueComponent = (
        <Text type="secondary">{t("EmptyInput_Label")}</Text>
    );
    switch (field.type) {
        case "NumericInput":
        case "Dropdown":
        case "TextArea":
        case "TextInput":
            if (!field.value) {
                return noValueComponent;
            }
            return <Text>{field.value}</Text>;
        case "CheckboxGroup":
            return (
                <CheckboxGroupInput
                    options={field.field.options.map((option) => ({
                        value: option,
                        label: option,
                    }))}
                    value={field.value}
                />
            );

        case "Effectiveness": {
            const nameMap: Record<EffectivenessFieldTypeAnswer, string> = {
                effective: field.field.effectiveOptionName,
                partiallyEffective: field.field.partiallyEffectiveOptionName,
                notEffective: field.field.notEffectiveOptionName,
            };
            const text = nameMap[field.value];
            return <Text>{text}</Text>;
        }
        case "RadioButtonGroup":
            return (
                <RadioButtonGroupInput
                    options={field.field.options.map((option) => ({
                        value: option,
                        label: option,
                    }))}
                    value={field.value}
                />
            );
        case "FileUpload":
            if (!field.value) {
                return <Text>{t("EmptyInput_Label")}</Text>;
            }
            return (
                <FileUploaderInput
                    savedBlobs={savedBlobs.filter((blob) =>
                        field.value.includes(blob.id)
                    )}
                    value={field.value}
                    disabled
                    hideMissing
                />
            );
        case "DataGrid":
            return null; // Not in MVP
        case "Heading":
            throw new Error("Expected parent component to handle Heading task");
        default:
            return assertNever(field);
    }
}
